import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import { gt } from 'ember-truth-helpers';
import type {
  TablePricingManagerCustomColumn,
  TablePricingManagerCustomRow,
} from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager';
import HotelPricingAssistantTableTooltipsTooltipCompetitorCell from 'lumen/components/hotel/pricing-assistant/table/tooltips/tooltip-competitor-cell';
import type { Rate } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { translateRoomCategory } from 'lumen/utils/pricing-assistant/open-pricing-utils';

import styles from './td-competitors-rate.module.css';

interface HotelPricingAssistantTableCellsTdCompetitorsRateSignature {
  Args: {
    column: TablePricingManagerCustomColumn;
    row: TablePricingManagerCustomRow;
    value: Rate;
  };
}

export default class HotelPricingAssistantTableCellsTdCompetitorsRate extends Component<HotelPricingAssistantTableCellsTdCompetitorsRateSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.args.column.date);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rate() {
    return this.args.value;
  }

  get showPriceChangeArrow(): boolean {
    const priceChange = this.args.value.priceChangeSinceYesterday;
    return priceChange !== null && priceChange !== 0 && !this.isPast;
  }

  @action
  onDoubleClick(): void {
    if (this.access.isStaffUser && console) {
      const dateStr = this.args.column.date.format('YYYY-MM-DD');
      const hotelName = this.rate.hotelinfo.name;
      const cemaCategory = this.rate.cemaCategory
        ? translateRoomCategory(this.intl, this.rate.cemaCategory)
        : 'N/A';

      console.group(
        `Debug: Pricing Manager Competitor Cell - ${hotelName} - ${cemaCategory} - ${dateStr}`,
      );

      if (console.table && this.rate) {
        console.log('Rate object:');
        console.table(this.rate.toJSON());
      } else {
        console.log('No console.table available or rate is undefined');
      }

      console.groupEnd();
    }
  }

  <template>
    {{! template-lint-disable no-invalid-interactive }}
    <div
      class="text-200--regular flex justify-end gap-100 truncate tabular-nums
        {{if this.isPast 'text-disabled'}}
        "
      {{on "dblclick" this.onDoubleClick}}
    >
      {{this.rate.formattedRate}}
      <div class={{styles.cell-icon-block}}>
        {{#if this.showPriceChangeArrow}}
          <PrismIcon
            @color={{if
              (gt this.rate.priceChangeSinceYesterday 0)
              "on-success"
              "on-critical"
            }}
            @name={{if
              (gt this.rate.priceChangeSinceYesterday 0)
              "arrow-up"
              "arrow-down"
            }}
            @size="extra-small"
          />
        {{/if}}
      </div>
      <HotelPricingAssistantTableTooltipsTooltipCompetitorCell
        @date={{@column.date}}
        @rate={{this.rate}}
        @refDaysAgo={{@column.refDaysAgo}}
      />
    </div>
  </template>
}
